import { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import DnsIcon from "@mui/icons-material/Dns";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ResultCard from "./ResultCard";
import { ResultData } from "./types";
import { updateDNS, validateDNS } from "../../apis/postProvisionApi/postProvisionApi"; 

type DNSBindingProps = {
  clientId: any;
  clientName: string;
  onComplete?: () => void;
};

export default function DNSBinding({ clientId, clientName, onComplete }: DNSBindingProps) {
  const [subdomainPrefix, setSubdomainPrefix] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [results, setResults] = useState<ResultData | null>(null);
  const [validationResults, setValidationResults] = useState<ResultData | null>(null);
  const [error, setError] = useState("");
  const [expandedResults, setExpandedResults] = useState<{ [key: string]: boolean }>({});

  const validateForm = () => {
    if (!subdomainPrefix.trim()) {
      setError("Subdomain prefix is required");
      return false;
    }
    if (!clientId) {
      setError("Client ID is required");
      return false;
    }
    if (!clientName.trim()) {
      setError("Client Name is required");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (action: "update" | "validate") => {
    if (!validateForm()) return;
    action === "update" ? setLoading(true) : setValidating(true);

    try {
      if (action === "update") {
        const data = await updateDNS({ subdomainPrefix, clientId, clientName });
        setResults(data);

        if (!data.success) {
          setError(data.error || "Operation failed");
        } else if (onComplete) {
          onComplete();
        }
      } else {
        const data = await validateDNS({ subdomainPrefix });
        setValidationResults(data);

        if (!data.success) {
          setError(data.error || "Validation failed");
        }
      }
    } catch (err: any) {
      setError(`Failed to ${action} DNS records: ${err.message}`);
    } finally {
      setLoading(false);
      setValidating(false);
    }
  };

  const toggleExpanded = (resultType: string, index: number) => {
    const key = `${resultType}-${index}`;
    setExpandedResults((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <DnsIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography variant="h4" component="h1">
          DNS Mapping Management
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Configuring DNS Records for p360connect.biz
          </Typography>

          <TextField
            fullWidth
            label="Subdomain Prefix"
            value={subdomainPrefix}
            onChange={(e) => setSubdomainPrefix(e.target.value)}
            placeholder="staging-"
            sx={{ mb: 3 }}
          />

          <Typography variant="body2">
            <strong>Client ID:</strong> {clientId}
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
            <strong>Client Name:</strong> {clientName}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => handleSubmit("update")}
              disabled={loading || validating}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              size="large"
            >
              {loading ? "Updating DNS..." : "Update DNS Records"}
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleSubmit("validate")}
              disabled={loading || validating}
              startIcon={validating ? <CircularProgress size={20} /> : <VisibilityIcon />}
              size="large"
            >
              {validating ? "Validating..." : "Validate DNS Records"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {results && (
        <ResultCard
          title="DNS Update Results"
          results={results}
          resultType="update"
          expandedResults={expandedResults}
          onToggle={toggleExpanded}
        />
      )}

      {validationResults && (
        <ResultCard
          title="DNS Validation Results"
          results={validationResults}
          resultType="validation"
          expandedResults={expandedResults}
          onToggle={toggleExpanded}
        />
      )}
    </Container>
  );
}
